import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'wasp/client/operations';
import { getPublicProfile, getUserDiscussions } from 'wasp/client/operations';
import { routes, Link } from 'wasp/client/router';
import { UserAvatar, UserFlairBadge } from '../components/common';
import { DiscussionCard } from '../discussions/DiscussionCard';
import type { PublicProfile } from './types';

const PAGE_SIZE = 10;

function ProfileSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="rounded-xl border border-neutral-200/60 dark:border-neutral-700/50 bg-white dark:bg-boxdark p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-full bg-neutral-200 dark:bg-neutral-700" />
          <div className="flex-1 space-y-3">
            <div className="h-6 w-40 rounded bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-4 w-64 rounded bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-4 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 rounded-xl border border-neutral-200/60 dark:border-neutral-700/50 bg-white dark:bg-boxdark"
          />
        ))}
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 text-5xl text-neutral-300 dark:text-neutral-600">
        ?
      </div>
      <h1 className="mb-2 text-xl font-bold text-neutral-900 dark:text-white">
        User not found
      </h1>
      <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
        This profile doesn&apos;t exist or has been removed.
      </p>
      <Link
        to={routes.DiscussionsRoute.to}
        className="text-sm font-medium text-primary hover:underline"
      >
        Browse discussions
      </Link>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 text-5xl text-danger-400">!</div>
      <h1 className="mb-2 text-xl font-bold text-neutral-900 dark:text-white">
        Something went wrong
      </h1>
      <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm font-medium text-primary hover:underline"
      >
        Try again
      </button>
    </div>
  );
}

function ProfileHeader({ profile }: { profile: PublicProfile }) {
  const memberSince = new Date(profile.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="rounded-xl border border-neutral-200/60 dark:border-neutral-700/50 bg-white dark:bg-boxdark p-6 mb-6">
      <div className="flex items-start gap-4">
        <UserAvatar username={profile.username} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-neutral-900 dark:text-white">
              {profile.username || 'Anonymous'}
            </h1>
            <UserFlairBadge flair={profile.flair?.tier} />
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {profile.stats.discussions} discussions &middot;{' '}
            {profile.stats.comments} comments &middot; Member since{' '}
            {memberSince}
          </p>
          {profile.aboutMe && (
            <p className="mt-3 max-w-prose break-words text-sm leading-relaxed text-neutral-700 dark:text-neutral-300 line-clamp-6">
              {profile.aboutMe}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { username } = useParams();
  const [page, setPage] = useState(0);

  // Reset pagination when navigating between profiles
  useEffect(() => {
    setPage(0);
  }, [username]);

  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery(getPublicProfile, { username: username! });

  const {
    data: discussionsData,
    isLoading: discussionsLoading,
    isFetching: discussionsFetching,
  } = useQuery(getUserDiscussions, {
    username: username!,
    skip: page * PAGE_SIZE,
    take: PAGE_SIZE,
  });

  if (profileLoading) {
    return (
      <div className="mx-auto max-w-[960px] px-4 py-6">
        <ProfileSkeleton />
      </div>
    );
  }

  if (profileError) {
    const is404 =
      profileError instanceof Error &&
      'statusCode' in profileError &&
      (profileError as { statusCode: number }).statusCode === 404;

    if (is404) {
      return (
        <div className="mx-auto max-w-[960px] px-4 py-6">
          <NotFoundState />
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-[960px] px-4 py-6">
        <ErrorState
          message={profileError.message || 'Failed to load profile'}
          onRetry={() => refetchProfile()}
        />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="mx-auto max-w-[960px] px-4 py-6">
      <ProfileHeader profile={profile} />

      <h2 className="mb-4 text-base font-semibold text-neutral-700 dark:text-neutral-200">
        Discussions
      </h2>

      {discussionsLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl border border-neutral-200/60 dark:border-neutral-700/50 bg-white dark:bg-boxdark"
            />
          ))}
        </div>
      ) : discussionsData && discussionsData.discussions.length > 0 ? (
        <div className="space-y-4">
          {discussionsData.discussions.map((discussion) => (
            <DiscussionCard
              key={discussion.id}
              discussion={discussion}
              showAuthor={false}
            />
          ))}

          {/* Pagination — only show when there are multiple pages */}
          {(discussionsData.hasMore || page > 0) && (
            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || discussionsFetching}
                className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-400 dark:hover:bg-neutral-800"
              >
                Previous
              </button>
              <span className="text-xs text-neutral-400 dark:text-neutral-500">
                Page {page + 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!discussionsData.hasMore || discussionsFetching}
                className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-400 dark:hover:bg-neutral-800"
              >
                Next
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-neutral-200/60 dark:border-neutral-700/50 bg-white dark:bg-boxdark py-16 text-center">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No discussions yet.
          </p>
        </div>
      )}
    </div>
  );
}
